// Which storefront — if any — is this page being served for?
//
// A tenant site lives on its own subdomain (frst-kool.example.com). The app
// itself lives on the bare host, or on www/app. Everything below works off
// window.location, so no build-time configuration is needed to switch
// between localhost, an ngrok tunnel and production.
//
// REACT_APP_ROOT_DOMAIN pins the apex when the app is served from a host
// with more labels than the heuristic can infer (e.g. app.eu.example.com).

// Labels the platform answers on — never a tenant.
const PLATFORM_LABELS = new Set(["www", "app", "admin", "portal", "api", "merchant", "dashboard", "staging", "dev"]);

// Hosts this platform itself answers on.
//
// digitalhouse.digitalhouse.ae has three labels, which the heuristic below
// would read as the storefront "digitalhouse" — the same mistake that made
// the Hostinger preview domain render the site-not-found page. An apex whose
// own name sits in a subdomain position has to be stated, because nothing in
// the shape of the host distinguishes it from shop.example.com.
//
// REACT_APP_ROOT_DOMAIN does the same job, but it only takes effect if the
// build receives it; naming the host here means the deployment is correct
// even when the platform passes environment variables to the running process
// and not to the build that produced the bundle.
const PLATFORM_HOSTS = new Set([
    "digitalhouse.digitalhouse.ae",
]);

// Hosts that hand out a generated third-level name to each deployment.
//
// The three-label heuristic below assumes the first label of a three-label
// host is a storefront, which is true for shop.example.com and wrong for
// every one of these: lime-hawk-319221.hostingersite.com is the platform
// itself, not a tenant called "lime-hawk-319221". Reading it as a tenant
// sends the whole deployment to the storefront lookup, which finds no such
// company and renders "الموقع غير موجود" over a perfectly healthy server.
//
// For these suffixes the apex is the suffix plus the generated label, so a
// storefront would have to be shop.lime-hawk-319221.hostingersite.com.
const SHARED_HOST_SUFFIXES = [
    "hostingersite.com",
    "trycloudflare.com",
    "ngrok-free.app",
    "ngrok.io",
    "ngrok.app",
    "vercel.app",
    "netlify.app",
    "onrender.com",
    "railway.app",
    "fly.dev",
    "pages.dev",
    "github.io",
];

// The apex for a host on one of the shared suffixes above, or null.
const sharedHostApex = (host) => {
    for (const suffix of SHARED_HOST_SUFFIXES) {
        if (host !== suffix && host.endsWith(`.${suffix}`)) {
            const labels = host.split(".");
            const suffixLabels = suffix.split(".").length;
            // suffix + the one generated label the provider assigned.
            return labels.slice(-(suffixLabels + 1)).join(".");
        }
    }
    return null;
};

const rootDomainOverride = (process.env.REACT_APP_ROOT_DOMAIN || "").trim().toLowerCase();

// Hosts with no dots (localhost) and raw IPs can never carry a subdomain.
const isBareHost = (hostname) =>
    !hostname.includes(".") || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

export function getTenantSlug(hostname = window.location.hostname) {
    const host = String(hostname).toLowerCase();
    if (isBareHost(host)) return null;
    if (PLATFORM_HOSTS.has(host)) return null;

    const labels = host.split(".");

    // A storefront under a declared platform host.
    for (const apex of PLATFORM_HOSTS) {
        if (host.endsWith(`.${apex}`)) {
            const first = host.slice(0, -(apex.length + 1)).split(".")[0];
            return PLATFORM_LABELS.has(first) ? null : first || null;
        }
    }

    if (rootDomainOverride) {
        if (!host.endsWith(`.${rootDomainOverride}`)) return null;
        const prefix = host.slice(0, -(rootDomainOverride.length + 1));
        const first = prefix.split(".")[0];
        return PLATFORM_LABELS.has(first) ? null : first || null;
    }

    // "<shop>.localhost" — how subdomains are reached in local development.
    if (labels[labels.length - 1] === "localhost") {
        const first = labels[0];
        return labels.length >= 2 && !PLATFORM_LABELS.has(first) ? first : null;
    }

    // A generated deployment host is the platform, not a storefront.
    const apex = sharedHostApex(host);
    if (apex) {
        if (host === apex) return null;
        const first = host.slice(0, -(apex.length + 1)).split(".")[0];
        return PLATFORM_LABELS.has(first) ? null : first || null;
    }

    // Otherwise a tenant needs at least three labels (shop.example.com);
    // example.com on its own is the platform.
    if (labels.length < 3) return null;
    const first = labels[0];
    return PLATFORM_LABELS.has(first) ? null : first;
}

// The apex the platform is reachable on, used to build storefront URLs.
export function getRootHost(location = window.location) {
    if (rootDomainOverride) return `${rootDomainOverride}${location.port ? `:${location.port}` : ""}`;
    const host = String(location.hostname).toLowerCase();
    for (const apex of PLATFORM_HOSTS) {
        if (host === apex || host.endsWith(`.${apex}`)) {
            return `${apex}${location.port ? `:${location.port}` : ""}`;
        }
    }
    const shared = sharedHostApex(host);
    if (shared) return `${shared}${location.port ? `:${location.port}` : ""}`;
    const slug = getTenantSlug(host);
    const bare = slug ? host.slice(slug.length + 1) : host;
    return `${bare}${location.port ? `:${location.port}` : ""}`;
}

// Public address of a storefront.
//
// The subdomain form, https://<slug>.<apex>, needs three things this
// deployment does not have: a wildcard DNS record, a wildcard vhost, and a
// wildcard certificate. Without them <slug>.<apex> does not resolve at all —
// so handing that link to a customer hands them a dead address, and the
// workspace was doing exactly that on its "موقع الشركة" screen.
//
// The path form works on any host, under the certificate the apex already
// has, so it is the default. Set REACT_APP_SUBDOMAIN_SITES=1 once wildcard
// DNS and a wildcard certificate are actually in place.
//
// A storefront already being viewed on its own subdomain keeps that address:
// there the subdomain demonstrably resolves, because the page loaded.
const SUBDOMAIN_SITES = String(process.env.REACT_APP_SUBDOMAIN_SITES || "").trim() === "1";

export function buildSiteUrl(slug, location = window.location) {
    if (!slug) return "";
    const root = getRootHost(location);
    if (SUBDOMAIN_SITES || getTenantSlug(location.hostname)) {
        return `${location.protocol}//${slug}.${root}`;
    }
    return `${location.protocol}//${root}/${slug}`;
}

// Whether this browser can actually resolve <slug>.<root>. Wildcard DNS for
// *.localhost is available in Chrome and Firefox but not everywhere, so the
// workspace keeps offering the path form as a fallback.
export function subdomainsResolveLocally(hostname = window.location.hostname) {
    return !isBareHost(hostname) || hostname === "localhost";
}

export const isTenantHost = () => Boolean(getTenantSlug());
